/**
 * ResultSkeletonCard — a glass placeholder with the same structure and size as
 * a UnifiedBetCard (header, price panel, alt lines, devig + consensus math,
 * and on wide layouts the sportsbook column), plus the shimmer primitives
 * shared by the search loader and MorphReveal.
 *
 * Motion is transform-only on purpose: web/custom.css and web/index.html force
 * low-opacity elements back to opacity 1, so opacity fades are unreliable here.
 */
import React, { useEffect, useRef, useState } from 'react';
import { View, StyleSheet, Animated, Easing, AccessibilityInfo, Platform } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { styles as cardStyles } from './unifiedBetCardStyles';

const CARD_RADIUS = StyleSheet.flatten(cardStyles.card)?.borderRadius ?? 16;
export const CARD_GAP = StyleSheet.flatten(cardStyles.card)?.marginBottom ?? 12;
// Width of the real card's sportsbook column (unifiedBetCardStyles.rightColumn).
const RIGHT_COLUMN = 280;

// Frosted glass over the page background.
export const CARD_FILL = ['rgba(255,255,255,0.075)', 'rgba(255,255,255,0.022)'];
export const SGP_CARD_FILL = ['rgba(167,139,250,0.16)', 'rgba(139,92,246,0.04)'];
// MorphReveal lays the placeholder over a real card, so it needs an opaque base under the glass.
export const OPAQUE_BASE = '#14141d';

const BONE = 'rgba(255,255,255,0.085)';
const BONE_SOFT = 'rgba(255,255,255,0.05)';
const WEB_GLASS = Platform.OS === 'web' ? {
  backdropFilter: 'blur(18px) saturate(140%)',
  WebkitBackdropFilter: 'blur(18px) saturate(140%)',
  boxShadow: '0 24px 60px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.08)',
} : {};

// One OS query and one listener for the whole app (not one per card); after
// the first answer, every later mount reads the cached value synchronously.
let _reducedMotion = null;
const _reducedMotionSubs = new Set();
let _reducedMotionStarted = false;
function startReducedMotionWatch() {
  if (_reducedMotionStarted) return;
  _reducedMotionStarted = true;
  const set = (v) => { _reducedMotion = !!v; _reducedMotionSubs.forEach((fn) => fn(_reducedMotion)); };
  try {
    AccessibilityInfo.isReduceMotionEnabled?.().then(set).catch(() => {});
    AccessibilityInfo.addEventListener?.('reduceMotionChanged', set);
  } catch { /* unsupported: animate */ }
}

/** True when the OS asks for reduced motion (web: prefers-reduced-motion). */
export function useReducedMotion() {
  const [reduced, setReduced] = useState(() => !!_reducedMotion);
  useEffect(() => {
    startReducedMotionWatch();
    _reducedMotionSubs.add(setReduced);
    if (_reducedMotion !== null) setReduced(_reducedMotion);
    return () => { _reducedMotionSubs.delete(setReduced); };
  }, []);
  return reduced;
}

/** One looping 0→1 value that drives every shimmer on screen in sync. */
export function useShimmerLoop(enabled = true) {
  const value = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    if (!enabled) { value.setValue(0); return undefined; }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(value, { toValue: 1, duration: 1500, easing: Easing.inOut(Easing.quad), useNativeDriver: false }),
        Animated.delay(250),
        Animated.timing(value, { toValue: 0, duration: 0, useNativeDriver: false }),
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [enabled, value]);
  return value;
}

/**
 * A band of light that crosses its parent — left→right by default, top→bottom
 * with `vertical` (pass the parent's height as `length`). Parent must clip.
 */
export function ShimmerSweep({ progress, width, length, vertical = false, band = 0.45, intensity = 0.075 }) {
  const span = vertical ? length : (length ?? width);
  if (!progress || !span) return null;
  const bandSize = Math.max(vertical ? 120 : 80, span * band);
  const offset = progress.interpolate({ inputRange: [0, 1], outputRange: [-bandSize, span + bandSize] });
  const gradient = (
    <LinearGradient
      colors={['rgba(255,255,255,0)', `rgba(255,255,255,${intensity})`, 'rgba(255,255,255,0)']}
      start={vertical ? { x: 0.5, y: 0 } : { x: 0, y: 0.5 }}
      end={vertical ? { x: 0.5, y: 1 } : { x: 1, y: 0.5 }}
      style={StyleSheet.absoluteFill}
    />
  );
  if (vertical) {
    return (
      <Animated.View pointerEvents="none" style={[s.sweepV, { height: bandSize, transform: [{ translateY: offset }] }]}>
        {gradient}
      </Animated.View>
    );
  }
  return (
    <Animated.View pointerEvents="none" style={[s.sweep, { width: bandSize, transform: [{ translateX: offset }, { skewX: '-18deg' }] }]}>
      {gradient}
    </Animated.View>
  );
}

// Deterministic variety so a stack of placeholders doesn't look stamped.
const TITLE_W = ['42%', '36%', '48%', '39%'];
const SUB_W = ['30%', '34%', '26%', '31%'];
const LINE_ROWS = [4, 3, 5, 4];
const SORT_PILLS = [48, 72, 44, 30, 28, 28, 30];

function Bar({ w, h = 12, soft, style }) {
  return <View style={[s.bar, soft && s.barSoft, { width: w, height: h }, style]} />;
}

function MetricTiles() {
  return (
    <View style={s.metricRow}>
      {[0, 1, 2].map((k) => (
        <View key={k} style={s.metricTile}>
          <Bar w="46%" h={9} soft />
          <Bar w="62%" h={18} style={{ marginTop: 8 }} />
        </View>
      ))}
    </View>
  );
}

function BookColumn({ rows = 12 }) {
  return (
    <View style={s.rightColumn}>
      <View style={s.rightHeader}>
        <Bar w="44%" h={13} />
        <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
          <Bar w={64} h={20} soft />
          <Bar w={64} h={20} soft />
        </View>
      </View>
      {Array.from({ length: rows }, (_, k) => (
        <View key={k} style={s.bookRow}>
          <View style={s.bookLogo} />
          <View style={{ flex: 1, marginLeft: 10 }}>
            <Bar w="58%" h={10} />
            <Bar w="34%" h={12} style={{ marginTop: 6 }} />
          </View>
          <Bar w={44} h={11} soft />
        </View>
      ))}
    </View>
  );
}

/** Just the bones, no frame. MorphReveal lays this over a real card. */
export function SkeletonBones({ seed = 0, compact = false }) {
  const i = Math.abs(seed) % 4;
  const left = (
    <View style={s.leftColumn}>
      <View style={s.headerRow}>
        <View style={s.avatar} />
        <View style={s.headerText}>
          <Bar w={TITLE_W[i]} h={16} />
          <Bar w={SUB_W[i]} h={11} soft style={{ marginTop: 9 }} />
          <View style={{ flexDirection: 'row', gap: 8, marginTop: 10 }}>
            <Bar w={46} h={16} soft />
            <Bar w={104} h={16} soft />
          </View>
        </View>
      </View>

      <View style={s.pricePanel}>
        <View style={{ flex: 1 }}>
          <Bar w="42%" h={24} />
          <Bar w="26%" h={10} soft style={{ marginTop: 10 }} />
        </View>
        <View style={{ alignItems: 'flex-end' }}>
          <Bar w={78} h={26} />
          <Bar w={58} h={10} soft style={{ marginTop: 10 }} />
        </View>
      </View>

      <View style={s.linesHeader}>
        <Bar w="30%" h={12} soft />
      </View>
      <View style={s.sortRow}>
        {SORT_PILLS.map((w, k) => <Bar key={k} w={w} h={22} soft />)}
      </View>
      {Array.from({ length: LINE_ROWS[i] }, (_, k) => (
        <View key={k} style={[s.lineRow, k === 0 && s.lineRowActive]}>
          <View style={{ flex: 1 }}>
            <Bar w="28%" h={13} />
            <Bar w="16%" h={9} soft style={{ marginTop: 7 }} />
          </View>
          <Bar w={52} h={14} />
        </View>
      ))}

      <View style={s.section}>
        <Bar w="26%" h={11} soft />
        <Bar w="92%" h={9} soft style={{ marginTop: 12 }} />
        <MetricTiles />
      </View>
      <View style={s.section}>
        <Bar w="30%" h={11} soft />
        <Bar w="88%" h={9} soft style={{ marginTop: 12 }} />
        <Bar w="64%" h={9} soft style={{ marginTop: 7 }} />
        <MetricTiles />
      </View>

      {compact && (
        <View style={s.booksRowCompact}>
          {[0, 1, 2, 3].map((k) => <View key={k} style={s.bookTile} />)}
        </View>
      )}
    </View>
  );
  if (compact) return <View style={s.inner}>{left}</View>;
  return (
    <View style={[s.inner, s.innerWide]}>
      {left}
      <BookColumn />
    </View>
  );
}

export default function ResultSkeletonCard({ shimmer, seed = 0, sgp = false, compact = false, style }) {
  const [height, setHeight] = useState(0);
  return (
    <View
      style={[s.card, WEB_GLASS, style]}
      onLayout={(e) => setHeight(e.nativeEvent.layout.height)}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <LinearGradient colors={sgp ? SGP_CARD_FILL : CARD_FILL} start={{ x: 0, y: 0 }} end={{ x: 1, y: 1 }} style={StyleSheet.absoluteFill} />
      {/* Glossy top sheen */}
      <LinearGradient colors={['rgba(255,255,255,0.09)', 'rgba(255,255,255,0)']} start={{ x: 0.5, y: 0 }} end={{ x: 0.5, y: 1 }} style={s.sheen} />
      <SkeletonBones seed={seed} compact={compact} />
      <ShimmerSweep progress={shimmer} length={height} vertical band={0.3} intensity={0.07} />
    </View>
  );
}

const s = StyleSheet.create({
  card: {
    width: '100%',
    borderRadius: CARD_RADIUS,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.10)',
    borderTopColor: 'rgba(255,255,255,0.22)',
    overflow: 'hidden',
    marginBottom: CARD_GAP,
  },
  sheen: { position: 'absolute', top: 0, left: 0, right: 0, height: 140 },
  inner: { width: '100%' },
  innerWide: { flexDirection: 'row' },
  leftColumn: { flex: 1, minWidth: 0, padding: 20 },
  rightColumn: {
    width: RIGHT_COLUMN,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255,255,255,0.07)',
    backgroundColor: 'rgba(0,0,0,0.12)',
    paddingVertical: 16,
    paddingHorizontal: 14,
  },
  rightHeader: { alignItems: 'center', paddingBottom: 14, marginBottom: 6, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.06)' },
  bookRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 10, borderBottomWidth: 1, borderBottomColor: 'rgba(255,255,255,0.04)' },
  bookLogo: { width: 30, height: 30, borderRadius: 8, backgroundColor: BONE },
  headerRow: { flexDirection: 'row', alignItems: 'flex-start' },
  avatar: { width: 52, height: 52, borderRadius: 26, backgroundColor: BONE },
  headerText: { flex: 1, marginLeft: 14 },
  bar: { borderRadius: 6, backgroundColor: BONE },
  barSoft: { backgroundColor: BONE_SOFT },
  pricePanel: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 18,
    padding: 18,
    minHeight: 96,
    borderRadius: 14,
    backgroundColor: 'rgba(255,255,255,0.045)',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  linesHeader: { marginTop: 18, paddingVertical: 10, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)' },
  sortRow: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginBottom: 12 },
  lineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 14,
    height: 54,
    marginBottom: 8,
    borderRadius: 10,
    backgroundColor: 'rgba(255,255,255,0.03)',
  },
  lineRowActive: { backgroundColor: 'rgba(139,92,246,0.14)', borderWidth: 1, borderColor: 'rgba(167,139,250,0.28)' },
  section: { marginTop: 18, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)' },
  metricRow: { flexDirection: 'row', gap: 10, marginTop: 14 },
  metricTile: { flex: 1, alignItems: 'center', paddingVertical: 12, borderRadius: 10, backgroundColor: 'rgba(255,255,255,0.03)' },
  booksRowCompact: { flexDirection: 'row', gap: 10, marginTop: 18, paddingTop: 16, borderTopWidth: 1, borderTopColor: 'rgba(255,255,255,0.05)' },
  bookTile: { flex: 1, height: 42, borderRadius: 10, backgroundColor: BONE_SOFT },
  sweep: { position: 'absolute', top: -20, bottom: -20, left: 0 },
  sweepV: { position: 'absolute', left: 0, right: 0, top: 0 },
});
